import type { Lesson, StudentProgress, Subject, Topic } from '@/types/index'

export type LessonStatus = 'not_started' | 'in_progress' | 'completed'

export interface OrderedLesson {
  lessonId: number
  title: string
  status: LessonStatus
}

export type SubjectLessonIndex = Map<string, OrderedLesson[]>

export interface SubjectResolution {
  lessonId: number | null
  title: string | null
  skippedCount: number
  gapLessonId: number | null
  gapTitle: string | null
  lessonDone: boolean
  gapDone: boolean
}

export function normalizeSubjectKey(name: string): string {
  return name.trim().toLocaleLowerCase('tr')
}

export function toStatusMap(progress: StudentProgress[]): Map<number, LessonStatus> {
  const map = new Map<number, LessonStatus>()
  progress.forEach((p) => {
    if (p.status === 'in_progress' || p.status === 'completed') {
      map.set(p.lesson_id, p.status)
    } else if (!map.has(p.lesson_id)) {
      map.set(p.lesson_id, 'not_started')
    }
  })
  return map
}

export function buildSubjectLessonIndex(
  subjects: Subject[],
  topics: Topic[],
  lessons: Lesson[]
): SubjectLessonIndex {
  const topicsById = new Map<number, Topic>()
  topics.forEach((t) => topicsById.set(t.id, t))

  const lessonsByTopic = new Map<number, Lesson[]>()
  lessons.forEach((l) => {
    if (!l.is_published) return
    const list = lessonsByTopic.get(l.topic_id) || []
    list.push(l)
    lessonsByTopic.set(l.topic_id, list)
  })
  lessonsByTopic.forEach((list) =>
    list.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
  )

  const index: SubjectLessonIndex = new Map()
  subjects.forEach((s) => {
    const subjectTopics = topics
      .filter((t) => t.subject_id === s.id)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    const ordered: OrderedLesson[] = []
    subjectTopics.forEach((t) => {
      ;(lessonsByTopic.get(t.id) || []).forEach((l) => {
        ordered.push({ lessonId: l.id, title: l.name, status: 'not_started' })
      })
    })
    index.set(normalizeSubjectKey(s.name), ordered)
  })
  return index
}

function firstGap(list: OrderedLesson[], until: number): OrderedLesson | null {
  for (let i = 0; i < until; i++) {
    if (list[i].status !== 'completed') return list[i]
  }
  return null
}

export function resolveSubject(
  index: SubjectLessonIndex,
  progress: Map<number, LessonStatus>,
  subjectName: string
): SubjectResolution | null {
  const list = index.get(normalizeSubjectKey(subjectName))
  if (!list || list.length === 0) return null

  const withProgress = list.map((l) => ({
    ...l,
    status: progress.get(l.lessonId) || 'not_started'
  }))

  let furthest = -1
  withProgress.forEach((l, pos) => {
    if (l.status !== 'not_started') furthest = pos
  })

  const empty: SubjectResolution = {
    lessonId: null,
    title: null,
    skippedCount: 0,
    gapLessonId: null,
    gapTitle: null,
    lessonDone: false,
    gapDone: false
  }

  let pointer = furthest + 1
  if (pointer >= withProgress.length) {
    const remaining = withProgress.find((l) => l.status !== 'completed')
    if (!remaining) return empty
    const before = withProgress.slice(0, withProgress.indexOf(remaining))
    const gap = firstGap(withProgress, withProgress.indexOf(remaining))
    return {
      ...empty,
      lessonId: remaining.lessonId,
      title: remaining.title,
      skippedCount: before.filter((l) => l.status !== 'completed').length,
      gapLessonId: gap?.lessonId ?? null,
      gapTitle: gap?.title ?? null,
      gapDone: false
    }
  }

  const target = withProgress[pointer]
  const gap = firstGap(withProgress, pointer)
  const skippedCount = withProgress
    .slice(0, pointer)
    .filter((l) => l.status !== 'completed').length

  return {
    lessonId: target.lessonId,
    title: target.title,
    skippedCount,
    gapLessonId: gap?.lessonId ?? null,
    gapTitle: gap?.title ?? null,
    lessonDone: target.status === 'completed',
    gapDone: gap ? gap.status === 'completed' : false
  }
}
